import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import MusicIcon from '../../../svgs/MusicIcon';
import { checkWordsInString } from '../../../utility/general';
import PanelUploadButton from '../../common/PanelUploadButton';
import SpinningLoading from '../../SpinningLoading';
import SearchInput from '../SearchInput';
import SongCollection from '../SongCollection';
import MusicSongItem from './MusicSongItem';

import type { AddSongToTrackOptions } from '../SongCollection';
import type { Song } from '@src/types.ts/story';
import type {
  ChangeEvent,
  Dispatch,
  MutableRefObject,
  SetStateAction,
} from 'react';

const MUSIC_SECTION_WIDTH_PX = 300;

type Props = {
  type: 'my-audio' | 'org-audio';
  setCurrentSong: Dispatch<SetStateAction<string>>;
  handleAddToTrack: (
    song: Song,
    options: AddSongToTrackOptions,
  ) => Promise<void>;
  audioRef: MutableRefObject<HTMLAudioElement | null>;
  currentSong: string;
  preloadAudio: (url: string) => void;
};

const AudioLibrary = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    setCurrentSong,
    handleAddToTrack,
    audioRef,
    currentSong,
    preloadAudio,
    type,
  } = props;

  const myAudios =
    (type === 'my-audio'
      ? videoCreator.story?.myAudios
      : videoCreator.organization?.organizationAudios) || [];

  const [search, setSearch] = useState<string>('');
  const updatingMetadata = myAudios.some(
    a => a.metadataStatus === 'generating' && !a.waveformUploadId,
  );

  useEffect(() => {
    for (const audio of myAudios) {
      const song = audio.song;
      preloadAudio(song.url);
      if (!videoCreator.audioTracksData[song.url]) {
        videoCreator.loadWaveformForSource({
          source: song.url,
          name: song.title,
        });
      }
    }
  }, [myAudios]);

  async function uploadAudio(e: ChangeEvent<HTMLInputElement>) {
    const files = [...(e.target.files || [])].slice(0, 3);

    if (!files.length) return;
    try {
      videoCreator.uploadingMyAudio = true;

      const uploadResults =
        await datoClientStore.audioRepository.uploadAudioFiles(files);
      const allAudios = [...uploadResults, ...(myAudios || [])];
      if (type === 'my-audio') {
        videoCreator.story = {
          ...videoCreator.story!,
          myAudios: allAudios,
        };
        await videoCreator.updateStory(videoCreator.story);
      } else if (type === 'org-audio') {
        videoCreator.organization = {
          ...videoCreator.organization!,
          organizationAudios: allAudios,
        };
        await datoClientStore.albumRepository.update(videoCreator.organization);
      }

      videoCreator.uploadingMyAudio = false;
    } catch (error) {
      videoCreator.uploadingMyAudio = false;
      videoCreator.showToast({
        state: 'error',
        message: 'Audio Upload failed, please contact arbor',
      });
    }
  }

  const audioFiltered = useMemo(() => {
    if (!search) return myAudios;

    return myAudios.filter(audio => {
      return (
        checkWordsInString(audio.title, search) ||
        checkWordsInString(audio.song.title, search) ||
        (audio.song.customData.artist &&
          checkWordsInString(audio.song.customData.artist, search)) ||
        checkWordsInString(audio.song.customData.songName, search)
      );
    });
  }, [myAudios, search]);

  return (
    <>
      <SearchInput
        placeholder="Search Audio"
        handleAction={setSearch}
        radius="var(--radius-input)"
        autoSubmitDelay={300}
      />
      <Collection>
        <Songs style={{ width: '100%' }}>
          <ButtonWrapper>
            <PanelUploadButton
              onClick={() => {
                if (!fileInputRef.current) return;
                fileInputRef.current.value = '';
                fileInputRef.current.click();
              }}
            >
              Upload Audio
            </PanelUploadButton>
            <File
              ref={fileInputRef}
              type="file"
              multiple
              accept="audio/mp3, audio/wav"
              onChange={uploadAudio}
            />
          </ButtonWrapper>
          <WarningNote>Upload up to three files at a time</WarningNote>
          {(videoCreator.uploadingMyAudio || updatingMetadata) && (
            <SpinningLoading
              Ico={<MusicIcon strokeColor="var(--primary)" />}
              text={
                <AnimationText>
                  <span>Uploading audio…</span>
                </AnimationText>
              }
            />
          )}
          {Boolean(audioFiltered.length) && (
            <SongCollection onAddToTrack={handleAddToTrack}>
              {({ handleSongSelect, isSongSelected }) =>
                audioFiltered
                  ?.filter(m => m.song.customData.duration)
                  .map(m => {
                    return (
                      <MusicSongItem
                        key={m.id}
                        audioRef={audioRef}
                        song={m.song}
                        title={m.title}
                        generatingMetadata={
                          m.metadataStatus === 'generating' &&
                          !m.waveformUploadId
                        }
                        currentSong={currentSong}
                        setCurrentSong={setCurrentSong}
                        mood=""
                        onSongClick={handleSongSelect}
                        customTimelineScale={
                          (MUSIC_SECTION_WIDTH_PX /
                            parseFloat(m.song.customData.duration)) *
                          1
                        }
                        isHighlighted={isSongSelected(m.song)}
                      />
                    );
                  })
              }
            </SongCollection>
          )}
        </Songs>
      </Collection>
    </>
  );
});

export default AudioLibrary;

const Collection = styled.div`
  display: flex;
  align-items: flex-start;
`;

const Songs = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-sm);
  text-transform: capitalize;
  .no-content {
    text-transform: none;
  }
`;

const ButtonWrapper = styled.div`
  margin-top: var(--spacing-sm);
  box-shadow: 0px 1px 0 0 var(--background);
  background: var(--background);
  z-index: 2;
  box-sizing: border-box;
  width: 100%;
`;

/* Hidden and opened by the button, so the button itself takes the pointer and
   the keyboard (PLAT-12136). It used to sit invisibly over the button. */
const File = styled.input`
  display: none;
`;

const AnimationText = styled.div`
  display: flex;
  flex-direction: column;
  text-transform: none;
`;

/* Block with its own bottom margin: the divider that used to sit under it
   carried the gap before the list (PLAT-12136). */
const WarningNote = styled.span`
  display: block;
  margin-bottom: var(--spacing-sm);
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-style: italic;
  font-weight: var(--font-weight-normal);
  text-transform: none;
`;
